/**
 * ExportDialog — Data export with scope selection
 *
 * Allows users to choose which scopes (transactions, budgets, goals, accounts,
 * categories) to export, with optional date range filtering. Downloads a
 * standardised fintrack-export JSON file.
 */

import { useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/features/hooks';
import { exportData } from '@/features/dataPortability/dataPortabilitySlice';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Download, Loader2, FileJson, Info } from 'lucide-react';
import {
  DataScope,
  ALL_DATA_SCOPES,
  DATA_SCOPE_LABELS,
  DATA_SCOPE_DESCRIPTIONS,
  type ExportFile,
} from '@/lib/types';

// Scopes the user can explicitly select (categories is auto-included when needed)
const SELECTABLE_SCOPES: DataScope[] = [
  DataScope.TRANSACTIONS,
  DataScope.BUDGETS,
  DataScope.GOALS,
  DataScope.ACCOUNTS,
  DataScope.CATEGORIES,
];

interface ExportDialogProps {
  trigger?: React.ReactNode;
}

export function ExportDialog({ trigger }: ExportDialogProps) {
  const dispatch = useAppDispatch();
  const { exportLoading } = useAppSelector((s) => s.dataPortability);
  const { toast } = useToast();

  const [open, setOpen] = useState(false);
  const [selectedScopes, setSelectedScopes] = useState<DataScope[]>(ALL_DATA_SCOPES);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const allSelected = selectedScopes.length === SELECTABLE_SCOPES.length;

  const toggleScope = (scope: DataScope) => {
    setSelectedScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope]
    );
  };

  const toggleAll = () => {
    setSelectedScopes(allSelected ? [] : ALL_DATA_SCOPES);
  };

  const handleExport = async () => {
    if (selectedScopes.length === 0) {
      toast({ title: 'Select at least one scope', variant: 'destructive' });
      return;
    }

    try {
      const file: ExportFile = await dispatch(
        exportData({ scopes: selectedScopes, dateFrom: dateFrom || undefined, dateTo: dateTo || undefined })
      ).unwrap();

      const blob = new Blob([JSON.stringify(file, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const dateStr = new Date().toISOString().slice(0, 10);
      a.download = `fintrack-export-${dateStr}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      const total = Object.values(file.metadata.counts).reduce((s, v) => s + v, 0);
      toast({
        title: 'Export successful',
        description: `Downloaded ${total} records across ${selectedScopes.length} scope(s).`,
      });
      setOpen(false);
    } catch (e: unknown) {
      toast({ title: 'Export failed', description: (e as string) || 'Unknown error', variant: 'destructive' });
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button variant="outline" data-testid="export-data-btn">
            <Download className="h-4 w-4 mr-2" />
            Export Data
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="export-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileJson className="h-5 w-5 text-primary" />
            Export Data
          </DialogTitle>
          <DialogDescription>
            Choose which data to export. The file follows the FinTrack portable JSON schema.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Select all */}
          <div className="flex items-center justify-between border-b pb-3">
            <Label className="text-sm font-medium">Data Scopes</Label>
            <button
              type="button"
              className="text-xs text-primary hover:underline"
              onClick={toggleAll}
            >
              {allSelected ? 'Deselect all' : 'Select all'}
            </button>
          </div>

          {/* Scope checkboxes */}
          <div className="space-y-3" data-testid="scope-checkboxes">
            {SELECTABLE_SCOPES.map((scope) => (
              <div key={scope} className="flex items-start gap-3">
                <Checkbox
                  id={`export-scope-${scope}`}
                  checked={selectedScopes.includes(scope)}
                  onCheckedChange={() => toggleScope(scope)}
                  data-testid={`export-scope-${scope}`}
                />
                <div className="space-y-0.5 min-w-0">
                  <Label htmlFor={`export-scope-${scope}`} className="font-medium cursor-pointer">
                    {DATA_SCOPE_LABELS[scope]}
                  </Label>
                  <p className="text-xs text-muted-foreground break-words">
                    {DATA_SCOPE_DESCRIPTIONS[scope]}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Date range (optional) */}
          <div className="space-y-2 pt-2 border-t">
            <div className="flex items-center gap-2">
              <Label className="text-sm font-medium">Date Range</Label>
              <Badge variant="outline" className="text-xs">Optional</Badge>
            </div>
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <Info className="h-3 w-3 shrink-0" />
              Filters transactions and budgets by date. Leave blank to export all.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="export-date-from" className="text-xs">From</Label>
                <Input
                  id="export-date-from"
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  data-testid="export-date-from"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="export-date-to" className="text-xs">To</Label>
                <Input
                  id="export-date-to"
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  data-testid="export-date-to"
                />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleExport}
            disabled={exportLoading || selectedScopes.length === 0}
            data-testid="export-confirm-btn"
          >
            {exportLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Exporting…
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-2" />
                Download JSON
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
